import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FileText, RefreshCw, Copy, Loader2, AlertTriangle, CheckCircle2, Info, Database } from 'lucide-react';
import { RESULT } from '@/constants/testIds';
import MapView from '@/components/MapView';
import IndexRadar from '@/components/IndexRadar';
import IndustryScoreCard from '@/components/IndustryScoreCard';
import MethodologyModal from '@/components/MethodologyModal';
import { useAuth } from '@/context/AuthContext';

const DOCUMENTED_METHODOLOGY = new Set([
  'demographic', 'demand', 'commercial_intensity', 'competition',
  'complementarity', 'accessibility', 'dynamism', 'tourism',
  'financial', 'information_quality', 'risk',
]);

const INDEX_META = {
  demographic: { name: 'Potencial demográfico', desc: 'Población, densidad y perfil compatible con el cliente objetivo.' },
  demand: { name: 'Demanda potencial', desc: 'Tamaño estimado del mercado objetivo.' },
  commercial_intensity: { name: 'Intensidad comercial', desc: 'Densidad y diversidad de establecimientos.' },
  competition: { name: 'Competencia', desc: 'Balance entre validación de demanda y saturación.' },
  complementarity: { name: 'Complementariedad', desc: 'Presencia de negocios que comparten cliente.' },
  accessibility: { name: 'Accesibilidad', desc: 'Densidad urbana y anclas comerciales.' },
  dynamism: { name: 'Dinamismo territorial', desc: 'Señales positivas vs. negativas recientes.' },
  tourism: { name: 'Turismo', desc: 'Aplicable solo si el giro depende del turismo.' },
  financial: { name: 'Viabilidad financiera', desc: 'Estimación preliminar con datos del usuario.' },
  information_quality: { name: 'Calidad de información', desc: 'Cobertura y actualidad de las fuentes.' },
  risk: { name: 'Riesgo territorial', desc: 'Alertas y factores de riesgo detectados.' },
};

const scoreColor = (s) => {
  if (s == null) return '#94a3b8';
  if (s >= 75) return '#15803d';
  if (s >= 55) return '#1e3a8a';
  if (s >= 40) return '#b45309';
  return '#b91c1c';
};

export default function AnalysisResult() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenDemo, setRegenDemo] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const load = () => {
    api.get(`/analyses/${id}`).then((r) => setA(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get(`/analyses/${id}`).then((r) => {
        setA(r.data);
        if (['completado', 'completado_con_advertencias', 'fallido'].includes(r.data.status)) {
          clearInterval(t);
        }
      });
    }, 4000);
    return () => clearInterval(t);
  }, [id]);

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem('strategia_token');
      const r = await fetch(`${api.defaults.baseURL}/analyses/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Error PDF');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `StrategIA_${a.name}.pdf`;
      link.click();
      toast.success('PDF descargado');
    } catch (e) {
      toast.error('Error al descargar PDF');
    }
  };

  const regenerate = async () => {
    await api.post(`/analyses/${id}/regenerate`);
    toast.info('Regeneración iniciada');
    load();
  };

  const regenerateDemographics = async () => {
    setRegenDemo(true);
    try {
      const r = await api.post(`/analyses/${id}/regenerate-demographics`);
      toast.success(`Demografía actualizada · Score ${r.data.score_general} · calidad ${r.data.demographics_quality}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error regenerando demografía');
    } finally { setRegenDemo(false); }
  };

  const duplicate = async () => {
    const r = await api.post(`/analyses/${id}/duplicate`);
    toast.success('Duplicado creado');
    window.location.href = `/analyses/${r.data.id}`;
  };

  if (loading || !a) return <div className="p-12 text-slate-500">Cargando análisis…</div>;

  const isProcessing = ['pendiente', 'recopilando', 'analizando', 'generando_pdf'].includes(a.status);
  const isDone = ['completado', 'completado_con_advertencias'].includes(a.status);
  const isFailed = a.status === 'fallido';

  const indices = a.indices || {};
  const competitors = a.establishments?.filter((e) => e.type === 'competencia_directa') || [];
  const indirect = a.establishments?.filter((e) => e.type === 'competencia_indirecta') || [];
  const complementary = a.establishments?.filter((e) => e.type === 'complementario') || [];
  const traffic = a.establishments?.filter((e) => e.type === 'generador_trafico') || [];

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="label-caps">Análisis territorial · {a.id.slice(0, 8)}</div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">{a.name}</h1>
          <div className="text-sm text-slate-600 mt-1">
            {a.location?.state_name} · {(a.location?.municipality_names || []).join(', ')} · Giro: <strong>{a.business_profile?.industry}</strong>
          </div>
        </div>
        <div className="flex gap-2">
          {isDone && (
            <Button onClick={downloadPdf} data-testid={RESULT.downloadPdfBtn} className="bg-blue-900 hover:bg-blue-800">
              <FileText size={14} className="mr-2" /> Descargar PDF
            </Button>
          )}
          <Button variant="outline" onClick={duplicate} data-testid={RESULT.duplicateBtn}>
            <Copy size={14} className="mr-2" /> Duplicar
          </Button>
          <Button variant="outline" onClick={regenerate} data-testid={RESULT.regenerateBtn}>
            <RefreshCw size={14} className="mr-2" /> Actualizar
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={regenerateDemographics} disabled={regenDemo} data-testid="regenerate-demographics-btn"
              title="Re-ejecuta solo INEGI + indices + score + PDF. No consume LLM ni Places.">
              {regenDemo ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Database size={14} className="mr-2" />}
              Regenerar demografía
            </Button>
          )}
        </div>
      </div>

      {/* Processing state */}
      {isProcessing && (
        <div className="bg-white border border-blue-200 p-6 mb-6 flex items-center gap-4">
          <Loader2 className="animate-spin text-blue-900" size={24} />
          <div>
            <div className="font-semibold text-slate-900">Análisis en proceso…</div>
            <div className="text-sm text-slate-600">{a.progress_message || 'Iniciando…'} (paso: {a.progress_step || 'preparando'})</div>
          </div>
        </div>
      )}
      {isFailed && (
        <div className="bg-red-50 border border-red-200 p-6 mb-6">
          <div className="flex items-center gap-2 text-red-800 font-semibold"><AlertTriangle size={18} /> Análisis fallido</div>
          <div className="text-sm text-red-700 mt-1">{a.progress_message}</div>
        </div>
      )}
      {a.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-4 mb-6 text-sm text-amber-900">
          <div className="font-semibold flex items-center gap-2"><AlertTriangle size={14} /> Advertencias:</div>
          <ul className="list-disc pl-5 mt-1">{a.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}

      {/* Score hero */}
      {isDone && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <div className="border-2 p-6 bg-white" style={{ borderColor: scoreColor(a.score_general) }}>
            <div className="label-caps flex items-center justify-between">
              <span>Score general</span>
              {a.sensitivity_check && (
                <span
                  data-testid="sensitivity-badge"
                  title={a.sensitivity_check.passed ? `Prueba de sensibilidad superada · Δ ${a.sensitivity_check.delta} pts entre 6 giros` : `Δ ${a.sensitivity_check.delta} pts — score poco diferenciado por giro`}
                  className={`text-[9px] font-semibold px-1.5 py-0.5 uppercase tracking-widest ${a.sensitivity_check.passed ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                >
                  {a.sensitivity_check.passed ? '✓' : '~'} Δ {a.sensitivity_check.delta}
                </span>
              )}
            </div>
            <div className="font-display text-7xl font-black tracking-tighter mt-2" data-testid={RESULT.scoreValue} style={{ color: scoreColor(a.score_general) }}>
              {a.score_general != null ? a.score_general.toFixed(0) : '—'}
            </div>
            <div className="mt-2 font-bold text-lg text-slate-900" data-testid={RESULT.scoreCategory}>{a.score_category}</div>
            <div className="text-xs text-slate-500 mt-1">Recomendación: <strong>{a.recommendation?.decision || '—'}</strong></div>
          </div>
          <div className="lg:col-span-2 border border-slate-200 p-6 bg-white">
            <div className="label-caps">Resumen ejecutivo</div>
            <p className="text-slate-700 mt-2 leading-relaxed text-[15px]">
              {a.executive_summary || 'Generando interpretación ejecutiva…'}
            </p>
          </div>
        </div>
      )}

      {isDone && (
        <>
          <IndustryScoreCard analysisId={id} businessProfile={a.business_profile} />
          <div className="mb-6" />
        </>
      )}

      {isDone && (
        <Tabs defaultValue="dashboard">
          <TabsList className="mb-4 bg-white border border-slate-200">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="map">Mapa</TabsTrigger>
            <TabsTrigger value="competition">Competencia</TabsTrigger>
            <TabsTrigger value="signals">Señales</TabsTrigger>
            <TabsTrigger value="recommendation">Recomendación</TabsTrigger>
            <TabsTrigger value="sources">Fuentes</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard" className="space-y-6">
            {a.isochrones?.modes && (
              <div className="border border-slate-200 bg-white p-6" data-testid="isochrones-populations">
                <div className="label-caps mb-4 flex items-center justify-between">
                  <span>Población alcanzable por isócrona (Google Routes × AGEBs INEGI)</span>
                  <span className="text-[10px] text-slate-500 normal-case tracking-normal">Método: intersección real polígono × AGEBs</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                      <th className="py-2">Modo</th>
                      {[5, 10, 15, 20, 30].map(m => <th key={m} className="py-2 text-right">{m} min</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(a.isochrones.modes).map(([mode, md]) => (
                      <tr key={mode} className="border-b border-slate-100" data-testid={`iso-row-${mode}`}>
                        <td className="py-2 font-semibold">{md.label} <span className="text-xs text-slate-400">({md.effective_speed_kmh} km/h)</span></td>
                        {[5, 10, 15, 20, 30].map(m => {
                          const iso = (md.isochrones || []).find(i => i.minutes === m);
                          return (
                            <td key={m} className="py-2 text-right">
                              {iso?.population != null ? (
                                <span className="font-semibold text-slate-900">{iso.population.toLocaleString('es-MX')}</span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                              {iso?.radius_km && <div className="text-[10px] text-slate-400">{iso.radius_km} km</div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 text-[11px] text-slate-500">
                  Cada celda muestra cuántas personas viven dentro del polígono real de la isócrona, cruzando con AGEBs urbanos del INEGI (Marco Geoestadístico 2025).
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-slate-200 bg-white p-6">
                <div className="label-caps mb-4">Radar de índices (11)</div>
                <IndexRadar indices={indices} />
              </div>
              <div className="border border-slate-200 bg-white p-6">
                <div className="flex items-baseline justify-between mb-4">
                  <div className="label-caps">Demografía</div>
                  {a.demographics?.radius_scope && (
                    <div className="flex items-center gap-2">
                      {a.demographics.radius_scope.method === 'inegi_ageb_intersection' && (
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5" data-testid="ageb-badge" title="Interpolación con AGEBs reales del INEGI Marco Geoestadístico">
                          ✓ AGEB reales · {a.demographics.radius_scope.agebs_intersected} polígonos
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-blue-900 bg-blue-50 px-2 py-0.5">
                        Radio {a.location?.radius_km} km · {(a.demographics.radius_scope.confidence * 100).toFixed(0)}% confianza
                      </div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {a.demographics?.radius_scope ? (
                    <>
                      <KPI label={`Población en radio (~${a.demographics.radius_scope.area_km2} km²)`} value={a.demographics.radius_scope.population_est?.toLocaleString('es-MX') || '—'} note={`Muni total: ${a.demographics.aggregated?.population?.toLocaleString('es-MX') || '—'}`} />
                      <KPI label="Hogares estimados en radio" value={a.demographics.radius_scope.households_est?.toLocaleString('es-MX') || '—'} />
                      <KPI label="Densidad hab/km²" value={a.demographics.radius_scope.density_hab_km2?.toFixed?.(1) || a.demographics.aggregated?.density?.toFixed?.(1) || '—'} note={a.demographics.radius_scope.coverage_pct != null ? `Cobertura AGEB: ${a.demographics.radius_scope.coverage_pct}%` : ''} />
                      <KPI label="PEA estimada en radio" value={a.demographics.radius_scope.pea_est?.toLocaleString('es-MX') || (a.demographics.radius_scope.method === 'inegi_ageb_intersection' && a.demographics.radius_scope.population_est && a.demographics.aggregated?.pea_pct ? Math.round(a.demographics.radius_scope.population_est * a.demographics.aggregated.pea_pct/100).toLocaleString('es-MX') : '—')} note={a.demographics.aggregated?.pea_pct ? `${a.demographics.aggregated.pea_pct}% del muni` : ''} />
                    </>
                  ) : (
                    <>
                      <KPI label="Población" value={a.demographics?.aggregated?.population?.toLocaleString('es-MX') || '—'} note={a.demographics?.source} />
                      <KPI label="Hogares" value={a.demographics?.aggregated?.households?.toLocaleString('es-MX') || '—'} />
                      <KPI label="Densidad hab/km²" value={a.demographics?.aggregated?.density?.toFixed(1) || '—'} />
                      <KPI label="PEA" value={a.demographics?.aggregated?.pea_pct ? `${a.demographics.aggregated.pea_pct}%` : '—'} />
                    </>
                  )}
                </div>
                {a.demographics?.radius_scope && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500" data-testid="radius-scope-note">
                    <strong>Método:</strong> {a.demographics.radius_scope.method_notes}
                    {a.demographics.radius_scope.top_agebs?.length > 0 && (
                      <div className="mt-2" data-testid="ageb-breakdown">
                        <strong>Top AGEBs dentro del radio:</strong>{' '}
                        {a.demographics.radius_scope.top_agebs.slice(0, 5).map((g, i) => (
                          <span key={i} className="inline-block mr-2">
                            {g.ageb} <span className="text-slate-400">({g.pop_in_radius.toLocaleString('es-MX')} hab, {(g.fraction_in_radius * 100).toFixed(0)}%)</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {a.demographics.radius_scope.warning && (
                      <div className="mt-1 text-amber-800"><strong>⚠</strong> {a.demographics.radius_scope.warning}</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(INDEX_META).map(([key, meta]) => {
                const idx = indices[key] || {};
                return (
                  <div key={key} data-testid={RESULT.indexCard(key)} className="border border-slate-200 bg-white p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <div className="font-semibold text-sm text-slate-900">{meta.name}</div>
                          {DOCUMENTED_METHODOLOGY.has(key) && (
                            <MethodologyModal
                              indexKey={key}
                              indexName={meta.name}
                              indexScore={idx.score != null ? Math.round(idx.score) : null}
                              confidence={idx.confidence}
                              analysisMeta={{ fetched_at: a.updated_at || a.created_at }}
                            />
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">{meta.desc}</div>
                      </div>
                      <div className="font-display text-3xl font-black" style={{ color: scoreColor(idx.score) }}>
                        {idx.score != null ? idx.score.toFixed(0) : '—'}
                      </div>
                    </div>
                    <div className="mt-3 h-1.5 bg-slate-100">
                      <div className="h-full transition-all" style={{ width: `${idx.score || 0}%`, backgroundColor: scoreColor(idx.score) }} />
                    </div>
                    <div className="text-xs text-slate-500 mt-2">
                      Confianza {Math.round((idx.confidence || 0) * 100)}%
                      {idx.note && <span> · {idx.note}</span>}
                    </div>
                    {idx.favorable?.filter(Boolean).length > 0 && (
                      <div className="text-xs text-green-800 mt-2">
                        <strong>+</strong> {idx.favorable.filter(Boolean).join('; ')}
                      </div>
                    )}
                    {idx.unfavorable?.filter(Boolean).length > 0 && (
                      <div className="text-xs text-red-700 mt-1">
                        <strong>−</strong> {idx.unfavorable.filter(Boolean).join('; ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="map">
            <div className="border border-slate-200 bg-white p-4" data-testid={RESULT.map}>
              <MapView analysis={a} />
            </div>
          </TabsContent>

          <TabsContent value="competition" className="space-y-6">
            <EstablishmentTable title="Competencia directa" items={competitors} threat="Alta" testidPrefix="direct" />
            <EstablishmentTable title="Competencia indirecta" items={indirect} threat="Media" testidPrefix="indirect" />
            <EstablishmentTable title="Negocios complementarios" items={complementary} threat="—" testidPrefix="complementary" />
            <EstablishmentTable title="Generadores de tráfico" items={traffic} threat="—" testidPrefix="traffic" />
          </TabsContent>

          <TabsContent value="signals">
            <div className="border border-slate-200 bg-white p-6">
              <div className="label-caps mb-4">Señales recientes y contexto territorial</div>
              {a.recent_signals?.length ? (
                <div className="space-y-3">
                  {a.recent_signals.map((s, i) => (
                    <div key={i} data-testid={RESULT.signalRow(i)} className="border-l-4 pl-4 py-2" style={{ borderColor: s.impact === 'positivo' ? '#15803d' : s.impact === 'negativo' ? '#b91c1c' : '#64748b' }}>
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-slate-900">{s.title}</div>
                        <span className="text-xs uppercase tracking-widest font-semibold px-2 py-0.5 bg-slate-100">{s.impact}</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-1">{s.summary}</div>
                      <div className="text-xs text-slate-400 mt-1">{s.date || '—'} · Fuente: {s.source}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-slate-500 text-sm">No se detectaron señales verificables.</div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="recommendation">
            <div className="border-2 border-blue-900 bg-white p-8">
              <div className="label-caps text-blue-900">Recomendación ejecutiva final</div>
              <div className="font-display text-4xl font-extrabold tracking-tight mt-2 text-slate-900">{a.recommendation?.decision}</div>
              <p className="text-slate-700 mt-4 text-[15px] leading-relaxed">{a.recommendation?.reasoning}</p>

              {a.recommendation?.conditions?.length > 0 && (
                <div className="mt-6">
                  <div className="label-caps text-slate-700 mb-2">Condiciones para avanzar</div>
                  <ul className="space-y-1">
                    {a.recommendation.conditions.map((c, i) => (
                      <li key={i} className="flex gap-2 text-sm"><CheckCircle2 size={16} className="text-green-700 mt-0.5 shrink-0" /> {c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {a.recommendation?.physical_validation?.length > 0 && (
                <div className="mt-6">
                  <div className="label-caps text-slate-700 mb-2">Plan de validación física</div>
                  <ul className="space-y-1">
                    {a.recommendation.physical_validation.map((c, i) => (
                      <li key={i} className="flex gap-2 text-sm"><Info size={16} className="text-blue-900 mt-0.5 shrink-0" /> {c}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sources">
            <div className="border border-slate-200 bg-white p-6">
              <div className="label-caps mb-4">Trazabilidad de datos</div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200"><th className="p-2">Fuente</th><th className="p-2">Categoría</th><th className="p-2">Calidad</th><th className="p-2">Consulta</th></tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100"><td className="p-2">INEGI Censo 2020</td><td className="p-2">Demografía</td><td className="p-2">{a.demographics?.quality}</td><td className="p-2 text-xs text-slate-500">{a.demographics?.fetched_at?.slice(0, 10)}</td></tr>
                  <tr className="border-b border-slate-100"><td className="p-2">DENUE</td><td className="p-2">Establecimientos</td><td className="p-2">{a.data_sources?.denue?.quality}</td><td className="p-2 text-xs text-slate-500">{a.data_sources?.denue?.fetched_at?.slice(0, 10)}</td></tr>
                  <tr className="border-b border-slate-100"><td className="p-2">SECTUR/DATATUR</td><td className="p-2">Turismo</td><td className="p-2">{a.tourism?.quality || 'no aplicable'}</td><td className="p-2 text-xs text-slate-500">—</td></tr>
                  <tr><td className="p-2">Motor de interpretación (Claude Sonnet 4.5)</td><td className="p-2">Interpretación / señales inferidas</td><td className="p-2">inferido</td><td className="p-2 text-xs text-slate-500">{a.updated_at?.slice(0, 10)}</td></tr>
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

const KPI = ({ label, value, note }) => (
  <div>
    <div className="label-caps">{label}</div>
    <div className="font-display text-2xl font-extrabold mt-1 text-slate-900">{value}</div>
    {note && <div className="text-xs text-slate-500 mt-1">{note}</div>}
  </div>
);

const EstablishmentTable = ({ title, items, threat, testidPrefix }) => (
  <div className="border border-slate-200 bg-white">
    <div className="p-4 border-b border-slate-200 flex items-center justify-between">
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{items.length} elemento(s)</div>
    </div>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left bg-slate-50 border-b border-slate-200">
          <th className="p-3 label-caps">Nombre</th>
          <th className="p-3 label-caps">Categoría</th>
          <th className="p-3 label-caps">Rating</th>
          <th className="p-3 label-caps">Reseñas</th>
          <th className="p-3 label-caps">Fuente</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-xs">Sin elementos detectados.</td></tr>}
        {items.map((e, i) => (
          <tr key={i} data-testid={`${testidPrefix}-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="p-3 font-medium text-slate-900">{e.name}{e.address && <div className="text-xs text-slate-500 mt-0.5">{e.address}</div>}</td>
            <td className="p-3 text-slate-700">{e.category}</td>
            <td className="p-3 text-slate-700 font-semibold">{e.rating ? `★ ${e.rating.toFixed(1)}` : '—'}</td>
            <td className="p-3 text-slate-500 text-xs">{e.reviews != null ? e.reviews.toLocaleString('es-MX') : '—'}</td>
            <td className="p-3 text-slate-500 text-xs">{e.source === 'google_places' ? 'Google Places' : 'DENUE'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
